<template>
  <div class="video-player-wrapper">
    <!-- DPlayer 容器 -->
    <div
      id="dplayer"
      ref="dplayerContainer"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchEnd"
    />

    <!-- 快进/快退视觉反馈浮层 (增加了 ref="seekOverlayRef") -->
    <transition name="seek-fade">
      <div
        v-if="seekFeedback.show"
        ref="seekOverlayRef"
        class="seek-overlay"
        :class="seekFeedback.type"
      >
        <i :class="seekFeedback.type === 'rewind' ? 'el-icon-d-arrow-left' : 'el-icon-d-arrow-right'" />
        <div class="seek-content">
          <!-- 移动端手势拖拽提示 -->
          <template v-if="seekFeedback.isTouch">
            <span class="seek-time">{{ formatTime(seekFeedback.targetTime) }} / {{ formatTime(seekFeedback.duration) }}</span>
            <span class="seek-delta">({{ seekFeedback.deltaSeconds >= 0 ? '+' : '' }}{{ seekFeedback.deltaSeconds }}s)</span>
          </template>
          <!-- 键盘连续点击提示 -->
          <template v-else>
            <span class="seek-text">
              {{ seekFeedback.type === 'rewind' ? '-' : '+' }}{{ seekFeedback.accumulatedSeconds }}s
            </span>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import flvjs from 'flv.js'
import DPlayer from 'dplayer'
import { videoUrl } from '@/api/vod'
import store from '@/store'

export default {
  name: 'VideoPlayerCard',
  props: {
    videoId: { type: String, required: true },
    videoData: { type: Object, required: true },
    userToken: { type: String, default: null },
    sendEvent: { type: Boolean, default: true }
  },
  data() {
    return {
      player: null,
      wsClient: null,
      wsUrl: null,
      wsReconnectLock: false,
      intervalEvent: null,
      danmakuApi: process.env.VUE_APP_SERVER_URL + '/api/comment/danmaku/',

      seekTimer: null,
      stepSeconds: 5,
      seekFeedback: {
        show: false,
        type: 'forward',
        isTouch: false,
        accumulatedSeconds: 0,
        targetTime: 0,
        duration: 0,
        deltaSeconds: 0
      },
      seekThrottleTimer: null, // 新增：用于实时预览 Seek 的节流控制
      touchState: {
        startX: 0,
        startY: 0,
        baseTime: 0,
        isSeeking: false,
        dragRate: 0.12 // 调整：略微降低灵敏度，让拖拽更平滑丝滑
      }
    }
  },
  watch: {
    videoId: {
      immediate: true,
      handler(val) {
        if (val) this.loadVideoSource()
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown)
    this.destroyResources()
  },
  methods: {
    destroyResources() {
      if (this.player) this.player.destroy()
      if (this.wsClient) this.wsClient.close()
      if (this.intervalEvent) clearInterval(this.intervalEvent)
      if (this.seekTimer) clearTimeout(this.seekTimer)
    },
    loadVideoSource() {
      this.destroyResources()
      videoUrl(this.videoId).then(res => {
        if (res.code === 0) {
          if (this.sendEvent) {
            this.wsUrl = this.getWsUrl(this.videoId)
            if (this.wsUrl != null) {
              this.initWebSocket()
            }
          }
          const { type, urls, currentTime } = res.data
          if (type === 'mp4') {
            urls.forEach(u => u.type = 'normal')
            this.initMp4Player(urls, currentTime)
          } else if (type === 'flv') {
            this.$message.error('flv player not implement')
          }
        }
      })
    },
    getWsUrl(videoId) {
      const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://'
      const host = window.location.host
      const token = store.getters.token
      if (token) {
        return `${protocol}${host}/ws/media?token=${token}&videoId=${videoId}`
      }

      return null
    },
    initMp4Player(urls, pos) {
      this.player = new DPlayer({
        container: this.$refs.dplayerContainer,
        lang: 'zh-cn',
        screenshot: true,
        hotkey: false,
        volume: 0.1,
        video: { pic: this.videoData.coverUrl, defaultQuality: 0, quality: urls },
        danmaku: { id: this.videoId, maximum: 10000, api: this.danmakuApi, token: this.userToken, bottom: '15%', unlimited: true }
      })
      this.player.seek(pos)
      this.bindEvents()
    },
    bindEvents() {
      let ended = false
      this.player.on('play', () => {
        if (this.sendEvent) {
          clearInterval(this.intervalEvent)
          this.intervalEvent = setInterval(() => {
            if (!ended) this.sendProgress(false)
          }, 5000)
        }
      })
      this.player.on('ended', () => {
        ended = true
        clearInterval(this.intervalEvent)
        this.sendProgress(true)
        this.$emit('ended')
      })
    },

    /* 手势处理 */
    handleTouchStart(e) {
      if (!this.player || !this.player.video || e.touches.length > 1) return

      const touch = e.touches[0]
      this.touchState.startX = touch.clientX
      this.touchState.startY = touch.clientY
      this.touchState.baseTime = this.player.video.currentTime
      this.touchState.isSeeking = false
    },

    handleTouchMove(e) {
      if (!this.player || !this.player.video || e.touches.length > 1) return

      const touch = e.touches[0]
      const deltaX = touch.clientX - this.touchState.startX
      const deltaY = touch.clientY - this.touchState.startY

      // 1. 判断是否触发横向 Seek 手势（增加门槛，防止误触）
      if (!this.touchState.isSeeking) {
        if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
          this.touchState.isSeeking = true
          // 开始拖拽时暂停视频，避免声音卡顿和画面冲突
          if (!this.player.video.paused) {
            this.player.pause()
          }
        } else {
          return
        }
      }

      // 阻止页面整体滚动
      e.preventDefault()

      const duration = this.player.video.duration || 0
      const deltaSeconds = Math.round(deltaX * this.touchState.dragRate)
      let targetTime = this.touchState.baseTime + deltaSeconds
      targetTime = Math.max(0, Math.min(targetTime, duration))

      // 更新提示框数据
      this.seekFeedback.show = true
      this.seekFeedback.isTouch = true
      this.seekFeedback.type = deltaSeconds >= 0 ? 'forward' : 'rewind'
      this.seekFeedback.targetTime = targetTime
      this.seekFeedback.duration = duration
      this.seekFeedback.deltaSeconds = deltaSeconds

      // 2. 核心突破：节流调用 player.seek() 实现画面实时跟随预览
      if (!this.seekThrottleTimer) {
        this.seekThrottleTimer = setTimeout(() => {
          if (this.player && this.player.video) {
            // fastSeek 性能比 seek 更高效（如果浏览器支持）
            if (typeof this.player.video.fastSeek === 'function') {
              this.player.video.fastSeek(targetTime)
            } else {
              this.player.seek(targetTime)
            }
          }
          this.seekThrottleTimer = null
        }, 40) // 约 25-30fps 的渲染频率，兼顾丝滑与性能
      }

      // 保证全屏下 DOM 层级正常
      this.$nextTick(() => {
        this.attachOverlayToDPlayer()
      })
    },

    handleTouchEnd() {
      if (this.touchState.isSeeking) {
        // 清理节流定时器
        if (this.seekThrottleTimer) {
          clearTimeout(this.seekThrottleTimer)
          this.seekThrottleTimer = null
        }

        // 最终精准 seek 到目标位置
        if (this.player) {
          this.player.seek(this.seekFeedback.targetTime)
          // 拖拽释放后恢复播放
          this.player.play()
        }

        this.touchState.isSeeking = false

        // 延时关闭 UI 提示框
        if (this.seekTimer) clearTimeout(this.seekTimer)
        this.seekTimer = setTimeout(() => {
          this.seekFeedback.show = false
          this.seekFeedback.isTouch = false
        }, 300)
      }
    },

    /**
     * 核心逻辑：将浮层动态 append 到 DPlayer 的内部 DOM 节点中
     * 这样不论是在原生全屏还是网页全屏，浮层都在全屏容器最顶层
     */
    attachOverlayToDPlayer() {
      const overlayEl = this.$refs.seekOverlayRef
      const dpContainer = this.$refs.dplayerContainer
      if (overlayEl && dpContainer) {
        // 获取 DPlayer 内部全屏容器
        const dpMask = dpContainer.querySelector('.dplayer') || dpContainer
        if (dpMask && overlayEl.parentElement !== dpMask) {
          dpMask.appendChild(overlayEl)
        }
      }
    },

    triggerSeek(type) {
      if (!this.player || !this.player.video) return

      if (this.seekFeedback.show && (this.seekFeedback.type !== type || this.seekFeedback.isTouch)) {
        this.seekFeedback.accumulatedSeconds = 0
      }

      this.seekFeedback.show = true
      this.seekFeedback.isTouch = false
      this.seekFeedback.type = type
      this.seekFeedback.accumulatedSeconds += this.stepSeconds

      const current = this.player.video.currentTime
      const duration = this.player.video.duration || 0

      if (type === 'forward') {
        this.player.seek(Math.min(current + this.stepSeconds, duration))
      } else {
        this.player.seek(Math.max(current - this.stepSeconds, 0))
      }

      this.$nextTick(() => {
        this.attachOverlayToDPlayer()
      })

      if (this.seekTimer) clearTimeout(this.seekTimer)
      this.seekTimer = setTimeout(() => {
        this.seekFeedback.show = false
        this.seekFeedback.accumulatedSeconds = 0
        this.seekTimer = null
      }, 600)
    },

    handleKeyDown(e) {
      const activeTag = document.activeElement.tagName.toLowerCase()
      if (activeTag === 'input' || activeTag === 'textarea' || document.activeElement.isContentEditable) {
        return
      }

      switch (e.code) {
        case 'ArrowLeft':
          e.preventDefault()
          this.triggerSeek('rewind')
          break
        case 'ArrowRight':
          e.preventDefault()
          this.triggerSeek('forward')
          break
        case 'Space':
          e.preventDefault()
          this.player.toggle()
          break
        case 'ArrowUp':
          e.preventDefault()
          this.player.volume(this.player.volume() + 0.1, true, false)
          break
        case 'ArrowDown':
          e.preventDefault()
          this.player.volume(this.player.volume() - 0.1, true, false)
          break
      }
    },

    formatTime(seconds) {
      if (isNaN(seconds) || seconds === null) return '00:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`
    },

    sendProgress(isEnded) {
      const jsonData = {
        type: 'progress',
        direction: 'c2s',
        data: {
          mediaId: this.videoId,
          mediaType: 1,
          currentTime: this.player ? this.player.video.currentTime : 0,
          ended: isEnded
        }
      }
      if (this.wsClient && this.wsClient.readyState === WebSocket.OPEN) {
        this.wsClient.send(JSON.stringify(jsonData))
      }
    },
    initWebSocket() {
      this.wsClient = new WebSocket(this.wsUrl)
      this.wsClient.onopen = () => this.$emit('ws-status', true)
      this.wsClient.onclose = () => {
        this.$emit('ws-status', false)
        this.reconnect()
      }
      this.wsClient.onerror = () => this.reconnect()
      this.wsClient.onmessage = (evt) => {
        const msg = JSON.parse(evt.data)
        this.$emit('update-view-count', msg.viewCount)
      }
    },
    reconnect() {
      if (this.wsReconnectLock) return
      this.wsReconnectLock = true
      setTimeout(() => {
        this.initWebSocket()
        this.wsReconnectLock = false
      }, 5000)
    }
  }
}
</script>

<style scoped>
.video-player-wrapper {
  position: relative;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
  outline: none;
  touch-action: pan-y;
}

#dplayer {
  height: 520px;
}

@media screen and (max-width: 768px) {
  #dplayer { height: 240px; }
}

/* 画面中央快进/快退提示样式 */
::v-deep .seek-overlay {
  position: absolute;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 12px 22px;
  border-radius: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
  pointer-events: none;
  /* 调至最高层级，防止被全屏蒙层挡住 */
  z-index: 99999 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

::v-deep .seek-overlay i {
  font-size: 26px;
  color: #409eff;
}

::v-deep .seek-overlay.rewind i {
  color: #e6a23c;
}

::v-deep .seek-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

::v-deep .seek-time {
  font-size: 16px;
  font-weight: bold;
}

::v-deep .seek-delta {
  font-size: 14px;
  color: #409eff;
}

::v-deep .seek-overlay.rewind .seek-delta {
  color: #e6a23c;
}

::v-deep .seek-text {
  font-size: 18px;
  font-weight: bold;
}

/* 淡入淡出动画 */
.seek-fade-enter-active, .seek-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.seek-fade-enter, .seek-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -40%) scale(0.85);
}

/* 隐藏网页全屏按钮 */
::v-deep .dplayer-full-in-icon {
  display: none !important;
}

::v-deep .dplayer-full-icon {
  margin-right: 5px !important;
}

::v-deep .seek-overlay {
  position: absolute;
  top: 50% !important;
  left: 50% !important;
  transform: translate3d(-50%, -50%, 0) !important; /* 改用 translate3d 触发 GPU 硬件加速 */
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 12px 22px;
  border-radius: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
  pointer-events: none;
  z-index: 99999 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

  /* 优化渲染性能 */
  will-change: transform, opacity;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}

::v-deep .dplayer-notice {
  display: none !important;
}
</style>
